import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Button, Field, NotProvided, Pagination, Table } from '@evogard/ui';
import { loadFailureMessage } from '@evogard/domain';
import { api } from '../../lib/api.js';
import { useSessionContext } from '../../lib/session.js';
import { useDebouncedTerm } from '../../lib/debounce.js';

/*
 * As duas listas que a régua de uma premiação MARCA — os tipos de boleto e os
 * produtos —, no formato de tabela do cadastro: busca, página e uma caixa por
 * linha. Pedido de 14/10/2026: os tipos de boleto eram digitados num campo de
 * texto, e um código digitado errado não dá erro nenhum — dá uma premiação que
 * não conta nada, descoberta no fim do mês.
 *
 * Os MARCADOS ficam numa lista própria, acima da tabela: a marcação pode estar
 * em outra página ou fora da busca, e tirá-la de vista faria um tipo marcado
 * continuar pagando sem ninguém ver.
 */

/** Um tipo de boleto marcado — o que o contrato da premiação grava. */
export interface BoletoTypeChoice {
  code: string;
  descricao: string | null;
}

/** Um produto marcado, com o valor ainda como texto do campo. */
export interface ProductChoice {
  code: string;
  label: string | null;
  amount: string;
}

/** A busca, a página e o tamanho dela — o mesmo trio das telas do ERP. */
function usePaging() {
  const [search, setSearch] = useState('');
  const term = useDebouncedTerm(search);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  return { search, setSearch, term, page, setPage, perPage, setPerPage };
}

/**
 * Os tipos de boleto que a premiação conta, marcados na lista da base.
 *
 * A lista sai do núcleo (`POST /erp/boleto-types/search`), do mais usado para o
 * menos, com quantos boletos cada tipo tem. Nenhum marcado RECUSA a apuração —
 * não conta tudo —, e a tela diz isso.
 */
export function BoletoTypePicker({
  selected,
  onChange,
}: {
  selected: BoletoTypeChoice[];
  onChange: (next: BoletoTypeChoice[]) => void;
}) {
  const { data: context } = useSessionContext();
  const tenantId = context?.currentTenant.id;
  const paging = usePaging();

  const query = useQuery({
    queryKey: [
      'erp',
      'tipos-de-boleto',
      tenantId,
      paging.term,
      paging.page,
      paging.perPage,
    ],
    queryFn: async () => {
      const r = await api.erp.searchBoletoTypes({
        body: {
          term: paging.term === '' ? undefined : paging.term,
          page: paging.page,
          perPage: paging.perPage,
        },
      });
      if (r.status !== 200) {
        throw new Error(loadFailureMessage(r.status, 'os tipos de boleto'));
      }
      return r.body;
    },
    placeholderData: (previous) => previous,
  });

  const marked = new Set(selected.map((t) => t.code));
  const toggle = (code: string, descricao: string | null, on: boolean) =>
    onChange(
      on
        ? [...selected.filter((t) => t.code !== code), { code, descricao }]
        : selected.filter((t) => t.code !== code),
    );

  return (
    <fieldset className="flex flex-col gap-3">
      <legend className="rotulo mb-2">Tipos de boleto que contam</legend>

      {selected.length === 0 ? (
        <p className="text-xs text-alerta">
          Nenhum tipo marcado — a premiação recusa a apuração, e não conta todo boleto.
        </p>
      ) : (
        <div className="flex flex-wrap gap-2">
          {selected.map((t) => (
            <span
              key={t.code}
              className="flex items-center gap-2 rounded-full border border-borda px-3 py-1 text-xs text-texto"
            >
              {t.descricao ?? 'sem descrição'}
              <span className="text-texto-suave">· {t.code}</span>
              <button
                type="button"
                className="text-texto-suave hover:text-texto"
                aria-label={`Desmarcar ${t.descricao ?? t.code}`}
                onClick={() => toggle(t.code, t.descricao, false)}
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}

      {query.isError ? (
        <NotProvided>
          {query.error.message} Marcar os tipos exige a permissão de ver o cadastro do
          ERP.
        </NotProvided>
      ) : (
        <div className="rounded-lg border border-borda-suave">
          <Table
            paginate={false}
            search={{
              label: 'Pesquisar tipos de boleto',
              placeholder: 'Descrição ou código do tipo',
              value: paging.search,
              onChange: (value) => {
                paging.setSearch(value);
                paging.setPage(1);
              },
            }}
            label="Tipos de boleto da base"
            items={query.data?.items ?? []}
            loading={query.isLoading}
            rowKey={(t) => t.code}
            empty={
              paging.term
                ? 'Nenhum tipo de boleto com essa busca.'
                : 'Os boletos desta base ainda não foram projetados no cadastro.'
            }
            columns={[
              {
                title: '',
                minWidth: '3rem',
                cell: (t) => (
                  <input
                    type="checkbox"
                    aria-label={`Marcar ${t.label ?? t.code}`}
                    checked={marked.has(t.code)}
                    onChange={(e) => toggle(t.code, t.label, e.target.checked)}
                  />
                ),
              },
              {
                title: 'Tipo',
                alignment: 'inicio' as const,
                minWidth: '16rem',
                cell: (t) => t.label ?? <NotProvided>sem descrição</NotProvided>,
              },
              {
                title: 'Código',
                minWidth: '6rem',
                cell: (t) => <span className="tabular-nums">{t.code}</span>,
              },
              {
                title: 'Boletos',
                alignment: 'fim' as const,
                minWidth: '7rem',
                cell: (t) => (
                  <span className="tabular-nums">
                    {t.boletos.toLocaleString('pt-BR')}
                  </span>
                ),
              },
            ]}
          />
          {query.data !== undefined && (
            <Pagination
              page={query.data.page}
              perPage={query.data.perPage}
              total={query.data.total}
              onChange={paging.setPage}
              onPerPageChange={(n) => {
                paging.setPerPage(n);
                paging.setPage(1);
              }}
              label="tipos de boleto"
              className="border-t border-borda-suave px-5 py-4"
            />
          )}
        </div>
      )}
    </fieldset>
  );
}

/**
 * Os produtos que a premiação paga, cada um com o valor por veículo.
 *
 * A lista é a dos veículos JÁ CONSULTADOS (`POST /erp/products/search`): o SGA
 * não tem catálogo de produto. O que nenhum consultado tem entra pelo código,
 * no fim.
 */
export function ProductPicker({
  selected,
  onChange,
  error,
}: {
  selected: ProductChoice[];
  onChange: (next: ProductChoice[]) => void;
  error?: string;
}) {
  const { data: context } = useSessionContext();
  const tenantId = context?.currentTenant.id;
  const paging = usePaging();
  const [manualCode, setManualCode] = useState('');
  const [manualLabel, setManualLabel] = useState('');

  const query = useQuery({
    queryKey: [
      'erp',
      'produtos-premiacao',
      tenantId,
      paging.term,
      paging.page,
      paging.perPage,
    ],
    queryFn: async () => {
      const r = await api.erp.searchProducts({
        body: {
          term: paging.term.length >= 2 ? paging.term : undefined,
          page: paging.page,
          perPage: paging.perPage,
        },
      });
      if (r.status !== 200) {
        throw new Error(loadFailureMessage(r.status, 'os produtos do cadastro do ERP'));
      }
      return r.body;
    },
    placeholderData: (previous) => previous,
  });

  const marked = new Set(selected.map((p) => p.code));
  const toggle = (code: string, label: string | null, on: boolean) =>
    onChange(
      on
        ? [...selected.filter((p) => p.code !== code), { code, label, amount: '' }]
        : selected.filter((p) => p.code !== code),
    );
  const setAmount = (code: string, amount: string) =>
    onChange(selected.map((p) => (p.code === code ? { ...p, amount } : p)));

  return (
    <fieldset className="flex flex-col gap-3">
      <legend className="rotulo mb-2">Quais produtos pagam, e quanto</legend>
      {error && <p className="text-xs text-erro">{error}</p>}

      {/* Os marcados, com o valor — em cima, porque é aqui que se digita. */}
      {selected.length === 0 ? (
        <p className="text-xs text-alerta">
          Nenhum produto marcado — a premiação recusa a apuração. Marque na tabela abaixo.
        </p>
      ) : (
        <div className="rounded-lg border border-borda-suave">
          <Table
            paginate={false}
            label="Produtos marcados"
            items={selected}
            rowKey={(p) => p.code}
            empty="Nenhum produto marcado."
            columns={[
              {
                title: 'Produto marcado',
                alignment: 'inicio' as const,
                minWidth: '16rem',
                cell: (p) => p.label ?? <NotProvided>sem descrição</NotProvided>,
              },
              {
                title: 'Código',
                minWidth: '6rem',
                cell: (p) => <span className="tabular-nums">{p.code}</span>,
              },
              {
                title: 'Paga por veículo (R$)',
                minWidth: '10rem',
                cell: (p) => (
                  <Field
                    label={`Valor de ${p.label ?? p.code}`}
                    labelHidden
                    inputMode="decimal"
                    placeholder="0,00"
                    value={p.amount}
                    onChange={(e) =>
                      setAmount(p.code, e.target.value.replace(/[^\d.,]/g, ''))
                    }
                  />
                ),
              },
              {
                title: '',
                alignment: 'fim' as const,
                minWidth: '7rem',
                cell: (p) => (
                  <Button
                    variant="contorno"
                    size="sm"
                    onClick={() => toggle(p.code, p.label, false)}
                  >
                    Remover
                  </Button>
                ),
              },
            ]}
          />
        </div>
      )}

      {query.isError ? (
        <NotProvided>
          {query.error.message} Escolher os produtos exige a permissão de ver o cadastro
          do ERP — dá para acrescentar pelo código abaixo.
        </NotProvided>
      ) : (
        <>
          {query.data !== undefined && (
            <p className="text-xs text-texto-suave">
              {query.data.vehiclesChecked.toLocaleString('pt-BR')} de{' '}
              {query.data.vehiclesTotal.toLocaleString('pt-BR')} veículos da base já
              tiveram os produtos consultados no ERP. A lista é o que eles têm.
            </p>
          )}
          <div className="rounded-lg border border-borda-suave">
            <Table
              paginate={false}
              search={{
                label: 'Pesquisar produtos',
                placeholder: 'Descrição ou código do produto',
                value: paging.search,
                onChange: (value) => {
                  paging.setSearch(value);
                  paging.setPage(1);
                },
              }}
              label="Produtos do cadastro"
              items={query.data?.items ?? []}
              loading={query.isLoading}
              rowKey={(p) => p.erpCode}
              empty={
                paging.term
                  ? 'Nenhum produto com essa busca nos veículos consultados.'
                  : 'Os veículos consultados não têm produto ligado no ERP.'
              }
              columns={[
                {
                  title: '',
                  minWidth: '3rem',
                  cell: (p) => (
                    <input
                      type="checkbox"
                      aria-label={`Marcar ${p.label ?? p.erpCode}`}
                      checked={marked.has(p.erpCode)}
                      onChange={(e) => toggle(p.erpCode, p.label, e.target.checked)}
                    />
                  ),
                },
                {
                  title: 'Produto',
                  alignment: 'inicio' as const,
                  minWidth: '18rem',
                  cell: (p) => p.label ?? <NotProvided>sem descrição</NotProvided>,
                },
                {
                  title: 'Código',
                  minWidth: '6rem',
                  cell: (p) => <span className="tabular-nums">{p.erpCode}</span>,
                },
                {
                  title: 'Veículos',
                  alignment: 'fim' as const,
                  minWidth: '7rem',
                  cell: (p) => <span className="tabular-nums">{p.vehiclesActive}</span>,
                },
              ]}
            />
            {query.data !== undefined && (
              <Pagination
                page={query.data.page}
                perPage={query.data.perPage}
                total={query.data.total}
                onChange={paging.setPage}
                onPerPageChange={(n) => {
                  paging.setPerPage(n);
                  paging.setPage(1);
                }}
                label="produtos"
                className="border-t border-borda-suave px-5 py-4"
              />
            )}
          </div>
        </>
      )}

      {/*
       * A ajuda mora FORA do campo: dentro, ela empurrava o campo do código
       * para cima e ele saía da linha do "Nome" e do botão. O `className` do
       * `Field` vai para o `<input>`, e não para o invólucro — por isso a
       * largura é de uma `<div>` em volta.
       */}
      <div className="flex flex-col gap-2">
        <p className="text-rotulo text-texto-suave">
          Produto que nenhum veículo consultado tem ainda entra pelo código.
        </p>
        <div className="flex flex-wrap items-end gap-3">
          <div className="w-36">
            <Field
              label="Código do produto"
              value={manualCode}
              onChange={(e) => setManualCode(e.target.value.trim())}
            />
          </div>
          <div className="w-72 max-w-full">
            <Field
              label="Nome"
              value={manualLabel}
              onChange={(e) => setManualLabel(e.target.value)}
            />
          </div>
          <Button
            variant="contorno"
            icon="mais"
            disabled={manualCode === '' || marked.has(manualCode)}
            onClick={() => {
              toggle(
                manualCode,
                manualLabel.trim() === '' ? null : manualLabel.trim(),
                true,
              );
              setManualCode('');
              setManualLabel('');
            }}
          >
            Acrescentar
          </Button>
        </div>
      </div>
    </fieldset>
  );
}
